import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowRight, BookOpen, WhatsappLogo } from "@phosphor-icons/react";
import { GoogleButton } from "../components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Soft client-side check — backend does the strict validation.
    const cleaned = whatsapp.trim();
    if (!/^\+?[0-9][0-9\s\-]{6,30}$/.test(cleaned)) {
      setError("Please enter a valid WhatsApp number (digits, optional + or spaces).");
      return;
    }
    setLoading(true);
    const res = await register({ name, email, password, whatsapp_number: cleaned });
    setLoading(false);
    if (res.ok) {
      toast.success("Account created. Let's get started.");
      nav("/dashboard", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="register-logo-link" className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-12 h-12 rounded-2xl neu-sm flex items-center justify-center text-indigo">
            <BookOpen size={22} weight="duotone" />
          </div>
          <div className="text-xl font-bold tracking-tight">Voxlingua</div>
        </Link>

        <form onSubmit={onSubmit} className="neu rounded-3xl p-10" data-testid="register-form">
          <div className="overline mb-3">Sign up</div>
          <h1 className="text-3xl md:text-4xl text-foreground font-bold tracking-tight leading-tight">Create your account.</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Already have one?{" "}
            <Link to="/login" data-testid="register-goto-login" className="font-bold text-foreground underline decoration-signal decoration-2 underline-offset-4 hover:text-signal">
              Log in
            </Link>
          </p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="overline mb-2 block" htmlFor="name">Name</label>
              <input
                id="name" type="text" required minLength={1}
                data-testid="register-name-input"
                value={name} onChange={(e) => setName(e.target.value)}
                className="neu-input w-full px-5 py-3.5 text-sm"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="overline mb-2 block" htmlFor="email">Email</label>
              <input
                id="email" type="email" required
                data-testid="register-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="neu-input w-full px-5 py-3.5 text-sm"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="overline mb-2 flex items-center gap-2" htmlFor="whatsapp">
                <WhatsappLogo size={12} weight="duotone" className="text-indigo" /> WhatsApp number
              </label>
              <input
                id="whatsapp" type="tel" required
                data-testid="register-whatsapp-input"
                value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
                className="neu-input w-full px-5 py-3.5 text-sm"
                placeholder="+1 555 123 4567"
                autoComplete="tel"
                inputMode="tel"
              />
              <div className="text-[11px] text-muted-foreground mt-1.5">
                Include your country code. Digits, spaces and dashes allowed.
              </div>
            </div>
            <div>
              <label className="overline mb-2 block" htmlFor="password">Password</label>
              <input
                id="password" type="password" required minLength={6}
                data-testid="register-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="neu-input w-full px-5 py-3.5 text-sm"
                placeholder="At least 6 characters"
              />
            </div>
          </div>

          {error && (
            <div data-testid="register-error" className="mt-5 text-sm text-red-500 neu-inset-sm px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="neu-btn-primary mt-8 w-full inline-flex items-center justify-center gap-2 px-8 py-4 font-medium"
          >
            {loading ? "Creating…" : "Create account"}
            {!loading && <ArrowRight size={16} weight="bold" />}
          </button>
          <GoogleButton testId="register-google-btn" />
        </form>
      </div>
    </div>
  );
}
